import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Invoice } from "./types";

interface AdminSectionProps {
  invoices: Invoice[];
  onAddInvoice: (invoice: Omit<Invoice, "id">) => void;
  onUpdateInvoice: (id: string, field: keyof Invoice, value: unknown) => void;
  onDeleteInvoice: (id: string) => void;
}

const formatDate = (value: Date | null) =>
  value ? value.toISOString().slice(0, 10) : "";

const AdminSection: React.FC<AdminSectionProps> = ({
  invoices,
  onAddInvoice,
  onUpdateInvoice,
  onDeleteInvoice,
}) => {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [invoiceErrors, setInvoiceErrors] = useState<{ amount?: boolean }>({});
  const [newInvoice, setNewInvoice] = useState({
    dateSent: new Date(),
    amount: 0,
    isPaid: false,
    remarks: "",
  });

  const handleAdd = () => {
    if (!newInvoice.amount || newInvoice.amount <= 0) {
      setInvoiceErrors({ amount: true });
      return;
    }
    onAddInvoice(newInvoice);
    setNewInvoice({
      dateSent: new Date(),
      amount: 0,
      isPaid: false,
      remarks: "",
    });
    setInvoiceErrors({});
    setIsAdding(false);
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptIcon fontSize="small" />
          <Typography
            variant="subtitle1"
            sx={{
              color: "text.secondary",
              mb: 0,
              textTransform: "uppercase"
            }}>
            {t("coachingNavigator.clientOverview.adminInvoices")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setIsAdding(!isAdding)}
        >
          {t("coachingNavigator.clientOverview.addInvoice")}
        </Button>
      </Box>
      {/* Add form */}
      {isAdding && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "#f3f3f3",
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 1,
            }}
          >
            <TextField
              label={t("coachingNavigator.clientOverview.dateSent")}
              type="date"
              value={formatDate(newInvoice.dateSent)}
              onChange={(e) =>
                setNewInvoice({
                  ...newInvoice,
                  dateSent: new Date(e.target.value),
                })
              }
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
              required
            />
            <TextField
              label={t("coachingNavigator.clientOverview.amount")}
              type="number"
              value={newInvoice.amount === 0 ? "" : newInvoice.amount}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0;
                setNewInvoice({ ...newInvoice, amount: val });
                if (val > 0) setInvoiceErrors({});
              }}
              size="small"
              required
              error={invoiceErrors.amount}
              helperText={
                invoiceErrors.amount
                  ? t("coachingNavigator.clientOverview.amountMustBePositive")
                  : ""
              } slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
            />
          </Box>
          <TextField
            label={t("coachingNavigator.clientOverview.remarks")}
            value={newInvoice.remarks}
            onChange={(e) =>
              setNewInvoice({ ...newInvoice, remarks: e.target.value })
            }
            size="small"
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={newInvoice.isPaid}
                  onChange={(e) =>
                    setNewInvoice({ ...newInvoice, isPaid: e.target.checked })
                  }
                  size="small"
                />
              }
              label={t("coachingNavigator.clientOverview.paid")}
            />
            <Button size="small" variant="contained" onClick={handleAdd}>
              {t("coachingNavigator.clientOverview.save")}
            </Button>
          </Box>
        </Box>
      )}
      {/* Invoice list */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {invoices.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              textAlign: "center",
              py: 2
            }}>
            {t("coachingNavigator.clientOverview.noInvoicesYet")}
          </Typography>
        ) : (
          invoices.map((invoice) => (
            <Box
              key={invoice.id}
              sx={{
                p: 1.5,
                bgcolor: "#f3f3f3",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="caption" sx={{
                  fontWeight: 500
                }}>
                  {dayjs(invoice.dateSent).format("MMM D, YYYY")}
                </Typography>
                <Typography variant="body2" sx={{
                  fontWeight: 600
                }}>
                  €{invoice.amount.toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Chip
                  label={
                    invoice.isPaid
                      ? t("coachingNavigator.clientOverview.paid")
                      : t("coachingNavigator.clientOverview.unpaid")
                  }
                  size="small"
                  color={invoice.isPaid ? "success" : "default"}
                  variant={invoice.isPaid ? "filled" : "outlined"}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Switch
                    checked={invoice.isPaid}
                    onChange={(e) =>
                      onUpdateInvoice(invoice.id, "isPaid", e.target.checked)
                    }
                    size="small"
                  />
                  <IconButton
                    size="small"
                    onClick={() => onDeleteInvoice(invoice.id)}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              </Box>
              {invoice.remarks && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    mt: 1,
                    pt: 1,
                    borderTop: 1,
                    borderColor: "divider"
                  }}>
                  {invoice.remarks}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default AdminSection;
